import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, Ticket, Loader2, Check, AlertCircle, Minus, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast, Toaster } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Embeddable Event Registration Widget
// Usage: <iframe src="https://your-domain.com/embed/events/{slug}?theme=light" width="400" height="600"></iframe>

const EmbedEventWidget = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  
  // Theme from query param (light or dark)
  const theme = searchParams.get('theme') || 'dark';
  const primaryColor = searchParams.get('color') || '#22c55e';
  const showDetails = searchParams.get('details') !== 'false';
  
  const [event, setEvent] = useState(null);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Registration state
  const [step, setStep] = useState(1); // 1: Tickets, 2: Info, 3: Success
  const [selectedTickets, setSelectedTickets] = useState({});
  const [buyerInfo, setBuyerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    country: 'Kenya'
  });
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);

  useEffect(() => {
    fetchEvent();
  }, [slug]);

  const fetchEvent = async () => {
    try {
      const response = await fetch(`${API}/universal-events/events/by-slug/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setEvent(data.event);
        setTicketTypes(data.ticket_types || []);
      } else {
        setError('Event not found');
      }
    } catch (err) {
      setError('Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const updateTicketQuantity = (ticketId, delta) => {
    setSelectedTickets(prev => {
      const current = prev[ticketId] || 0;
      const newQty = Math.max(0, Math.min(10, current + delta));
      if (newQty === 0) {
        const { [ticketId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [ticketId]: newQty };
    });
  };

  const getTotalAmount = () => {
    return Object.entries(selectedTickets).reduce((sum, [ticketId, qty]) => {
      const ticket = ticketTypes.find(t => t.id === ticketId);
      return sum + (ticket?.price || 0) * qty;
    }, 0);
  };

  const getTotalTickets = () => {
    return Object.values(selectedTickets).reduce((sum, qty) => sum + qty, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!buyerInfo.name || !buyerInfo.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const items = Object.entries(selectedTickets).map(([ticketId, qty]) => ({
        ticket_type_id: ticketId,
        quantity: qty,
        attendee_details: Array(qty).fill({ name: buyerInfo.name, email: buyerInfo.email })
      }));

      const response = await fetch(`${API}/universal-events/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_id: event.id,
          buyer_name: buyerInfo.name,
          buyer_email: buyerInfo.email,
          buyer_phone: buyerInfo.phone,
          buyer_country: buyerInfo.country,
          items
        })
      });

      if (response.ok) {
        const data = await response.json();
        setOrder(data);
        setStep(3);
        
        // Post message to parent window for tracking
        window.parent.postMessage({
          type: 'MICHEZONET_REGISTRATION_COMPLETE',
          orderId: data.id,
          eventId: event.id,
          eventName: event.name,
          totalAmount: getTotalAmount(),
          ticketCount: getTotalTickets()
        }, '*');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Registration failed');
      }
    } catch (err) {
      toast.error('Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Theme styles
  const styles = {
    container: theme === 'light' 
      ? 'bg-white text-gray-900' 
      : 'bg-zinc-900 text-white',
    card: theme === 'light'
      ? 'bg-gray-50 border-gray-200'
      : 'bg-zinc-800 border-zinc-700',
    input: theme === 'light'
      ? 'bg-white border-gray-300 text-gray-900 focus:ring-green-500'
      : 'bg-zinc-800 border-zinc-600 text-white focus:ring-green-500',
    text: theme === 'light' ? 'text-gray-600' : 'text-white/70',
    textMuted: theme === 'light' ? 'text-gray-500' : 'text-white/50',
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${styles.container}`}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: primaryColor }} />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${styles.container}`}>
        <div className="text-center p-6">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <p className="text-lg font-medium">{error || 'Event not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${styles.container}`}>
      <Toaster position="top-center" />
      
      <div className="p-4 max-w-md mx-auto">
        {/* Event Header */}
        {showDetails && step < 3 && (
          <div className="mb-6">
            <h1 className="text-xl font-bold mb-2" style={{ color: theme === 'light' ? '#111' : '#fff' }}>
              {event.name}
            </h1>
            <div className={`space-y-2 text-sm ${styles.text}`}>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" style={{ color: primaryColor }} />
                <span>{new Date(event.start_date).toLocaleDateString('en-US', { 
                  weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' 
                })}</span>
              </div>
              {event.venue_name && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" style={{ color: primaryColor }} />
                  <span>{event.venue_name}, {event.city}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 1: Select Tickets */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Ticket className="w-5 h-5" style={{ color: primaryColor }} />
              Select Tickets
            </h2>
            
            <div className="space-y-3 mb-6">
              {ticketTypes.map(ticket => (
                <div 
                  key={ticket.id} 
                  className={`p-4 rounded-xl border ${styles.card}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-medium">{ticket.name}</h3>
                      {ticket.description && (
                        <p className={`text-sm ${styles.textMuted}`}>{ticket.description}</p>
                      )}
                    </div>
                    <span className="font-bold" style={{ color: primaryColor }}>
                      KES {ticket.price.toLocaleString()}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={() => updateTicketQuantity(ticket.id, -1)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center border ${styles.card}`}
                      disabled={!selectedTickets[ticket.id]}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-8 text-center font-medium">
                      {selectedTickets[ticket.id] || 0}
                    </span>
                    <button
                      onClick={() => updateTicketQuantity(ticket.id, 1)}
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: primaryColor, color: '#000' }}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {getTotalTickets() > 0 && (
              <div className={`p-4 rounded-xl border mb-4 ${styles.card}`}>
                <div className="flex justify-between items-center">
                  <span className={styles.text}>{getTotalTickets()} ticket(s)</span>
                  <span className="text-xl font-bold">KES {getTotalAmount().toLocaleString()}</span>
                </div>
              </div>
            )}

            <Button 
              className="w-full font-semibold"
              style={{ backgroundColor: primaryColor, color: '#000' }}
              disabled={getTotalTickets() === 0}
              onClick={() => setStep(2)}
            >
              Continue
            </Button>
          </motion.div>
        )}

        {/* Step 2: Registration Info */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="text-lg font-semibold mb-4">Your Information</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${styles.text}`}>
                  Full Name *
                </label>
                <input
                  type="text"
                  value={buyerInfo.name}
                  onChange={(e) => setBuyerInfo(prev => ({ ...prev, name: e.target.value }))}
                  className={`w-full px-4 py-3 rounded-lg border ${styles.input}`}
                  placeholder="John Doe"
                  required
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${styles.text}`}>
                  Email *
                </label>
                <input
                  type="email"
                  value={buyerInfo.email}
                  onChange={(e) => setBuyerInfo(prev => ({ ...prev, email: e.target.value }))}
                  className={`w-full px-4 py-3 rounded-lg border ${styles.input}`}
                  placeholder="john@example.com"
                  required
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${styles.text}`}>
                  Phone
                </label>
                <input
                  type="tel"
                  value={buyerInfo.phone}
                  onChange={(e) => setBuyerInfo(prev => ({ ...prev, phone: e.target.value }))}
                  className={`w-full px-4 py-3 rounded-lg border ${styles.input}`}
                  placeholder="+254 712 345 678"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${styles.text}`}>
                  Country
                </label>
                <select
                  value={buyerInfo.country}
                  onChange={(e) => setBuyerInfo(prev => ({ ...prev, country: e.target.value }))}
                  className={`w-full px-4 py-3 rounded-lg border ${styles.input}`}
                >
                  <option value="Kenya">Kenya</option>
                  <option value="Uganda">Uganda</option>
                  <option value="Tanzania">Tanzania</option>
                  <option value="Rwanda">Rwanda</option>
                  <option value="Ethiopia">Ethiopia</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Order Summary */}
              <div className={`p-4 rounded-xl border ${styles.card}`}>
                <h3 className="font-medium mb-2">Order Summary</h3>
                {Object.entries(selectedTickets).map(([ticketId, qty]) => {
                  const ticket = ticketTypes.find(t => t.id === ticketId);
                  return (
                    <div key={ticketId} className={`flex justify-between text-sm ${styles.text}`}>
                      <span>{ticket?.name} × {qty}</span>
                      <span>KES {((ticket?.price || 0) * qty).toLocaleString()}</span>
                    </div>
                  );
                })}
                <div className="border-t mt-2 pt-2 flex justify-between font-bold">
                  <span>Total</span>
                  <span style={{ color: primaryColor }}>KES {getTotalAmount().toLocaleString()}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  type="button"
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setStep(1)}
                >
                  Back
                </Button>
                <Button 
                  type="submit"
                  className="flex-1 font-semibold"
                  style={{ backgroundColor: primaryColor, color: '#000' }}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Complete Registration'
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Step 3: Success */}
        {step === 3 && order && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8"
          >
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <Check className="w-8 h-8" style={{ color: primaryColor }} />
            </div>
            
            <h2 className="text-xl font-bold mb-2">Registration Complete!</h2>
            <p className={`mb-6 ${styles.text}`}>
              Confirmation sent to <span className="font-medium">{buyerInfo.email}</span>
            </p>
            
            <div className={`p-4 rounded-xl border ${styles.card} text-left mb-6`}>
              <h3 className="font-medium mb-2">{event.name}</h3>
              <div className={`text-sm space-y-1 ${styles.text}`}>
                <p>Order ID: {order.id?.slice(0, 8)}...</p>
                <p>Tickets: {getTotalTickets()}</p>
                <p>Total: KES {getTotalAmount().toLocaleString()}</p>
              </div>
            </div>
            
            <Button 
              onClick={() => {
                setStep(1);
                setSelectedTickets({});
                setBuyerInfo({ name: '', email: '', phone: '', country: 'Kenya' });
                setOrder(null);
              }}
              variant="outline"
              className="w-full"
            >
              Register Another
            </Button>
          </motion.div>
        )}

        {/* Footer */}
        <div className={`text-center mt-6 pt-4 border-t ${styles.textMuted} text-xs`} style={{ borderColor: theme === 'light' ? '#e5e7eb' : '#3f3f46' }}>
          Powered by <a href="https://michezonet.com" target="_blank" rel="noopener noreferrer" style={{ color: primaryColor }}>Michezonet</a>
        </div>
      </div>
    </div>
  );
};

export default EmbedEventWidget;
